import { useState, useEffect } from "react";
import { Button, IconButton } from "@molecules";
import { timeLabel } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useTranslation } from "react-i18next";
import { LocalTimespan } from "./timeAdjustmentHelpers";
import { useTimeAdjustmentSuggestion } from "./useTimeAdjustmentSuggestion";

type Props = {
  currentTimespan: LocalTimespan;
  defaultTimespan: LocalTimespan;
  hidden?: boolean;
  mutationType: "edit" | "create";
  onAcceptSuggestion: (suggestion: LocalTimespan) => void;
};

export const TimeAdjustmentSuggestion = ({
  currentTimespan,
  defaultTimespan,
  hidden,
  mutationType,
  onAcceptSuggestion,
}: Props) => {
  const { t } = useTranslation();
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const { suggestion, tryTriggerTrackingEvent } = useTimeAdjustmentSuggestion(currentTimespan, defaultTimespan);

  const handleAcceptSuggestion = () => {
    if (!suggestion) return;
    tryTriggerTrackingEvent(mutationType, "accept");
    onAcceptSuggestion({
      localStart: suggestion.localStart,
      localEnd: suggestion.localEnd,
    });
  };

  const handleDismissSuggestion = () => {
    if (!suggestion) return;
    tryTriggerTrackingEvent(mutationType, "dismiss");
    setSuggestionDismissed(true);
  };

  useEffect(() => {
    if (suggestion && !hidden) tryTriggerTrackingEvent(mutationType, "presented");
  }, [suggestion, hidden]);

  if (hidden || !suggestion || suggestionDismissed) {
    return null;
  }

  return (
    <div className="bg-beige-200 rounded-xl px-6 py-4 mx-4 mt-4 flex flex-col gap-4 items-start shadow-mapiq-small">
      <div className="flex items-start">
        <p>
          {t("timeslots:AdjustmentSuggestion_BeforeLabel")}{" "}
          <strong className="whitespace-nowrap">{timeLabel(suggestion.localStart, suggestion.localEnd, t)}</strong>{" "}
          {t("timeslots:AdjustmentSuggestion_AfterLabel")}
        </p>
        <IconButton
          icon="close"
          iconButton="tertiary"
          className="aspect-square"
          size="xsmall"
          onClick={handleDismissSuggestion}
        />
      </div>
      <div className="flex gap-4 items-center">
        <Button
          size="small"
          iconLeft="refresh"
          button="tertiary"
          onClick={handleAcceptSuggestion}>
          {t("timeslots:AdjustmentSuggestionCTA")}
        </Button>
      </div>
    </div>
  );
};
